import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, File as FileIcon, Paperclip, Trash2, Upload } from "lucide-react";

function isImage(ct) {
  return typeof ct === "string" && ct.startsWith("image/");
}

function formatSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function Attachments({ cardId, attachments, onChange, currentUser }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/cards/${cardId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...(attachments || []), data]);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${a.id}`);
      onChange((attachments || []).filter((x) => x.id !== a.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  // Build a fully-qualified URL using the same backend the rest of the app calls.
  const fullUrl = (url) => {
    if (!url) return "#";
    if (url.startsWith("http")) return url;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return base + url;
  };

  return (
    <div data-testid="attachments-section">
      <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
        <Paperclip className="h-3 w-3" /> Attachments
      </div>
      <div className="space-y-2">
        {(attachments || []).map((a) => (
          <div
            key={a.id}
            className="border border-zinc-200 flex items-stretch group"
            data-testid={`attachment-${a.id}`}
          >
            {isImage(a.content_type) ? (
              <a
                href={fullUrl(a.url)}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 h-16 w-16 border-r border-zinc-200 bg-zinc-50 overflow-hidden flex items-center justify-center"
              >
                <img
                  src={fullUrl(a.url)}
                  alt={a.filename}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </a>
            ) : (
              <div className="shrink-0 h-16 w-16 border-r border-zinc-200 bg-zinc-50 flex items-center justify-center">
                <FileIcon className="h-5 w-5 text-zinc-400" />
              </div>
            )}
            <div className="flex-1 min-w-0 p-2 flex flex-col justify-center">
              <a
                href={fullUrl(a.url)}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium truncate hover:underline"
                data-testid={`attachment-link-${a.id}`}
              >
                {a.filename}
              </a>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-tight">
                {formatSize(a.size)} · {a.uploaded_by_name || ""}
              </div>
            </div>
            <div className="flex items-center gap-1 pr-1">
              <a
                href={fullUrl(a.url)}
                target="_blank"
                rel="noreferrer"
                className="p-2 text-zinc-400 hover:text-black"
                aria-label="Open"
              >
                <Download className="h-4 w-4" />
              </a>
              {(!currentUser || a.uploaded_by === currentUser.id) && (
                <button
                  type="button"
                  onClick={() => remove(a)}
                  className="p-2 text-zinc-400 hover:text-red-500"
                  data-testid={`attachment-delete-${a.id}`}
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid="attachment-file-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-3 inline-flex items-center gap-2 border border-zinc-200 px-3 py-2 text-xs font-medium hover:border-black active:border-black disabled:opacity-50 transition-colors"
        data-testid="attachment-upload-button"
      >
        <Upload className="h-3.5 w-3.5" />
        {uploading ? "Uploading..." : "Upload file"}
      </button>
      <span className="ml-2 text-[10px] text-zinc-400 font-mono-tight uppercase tracking-widest">
        max 10MB
      </span>
    </div>
  );
}
